import type { Attachment } from '@pa/core';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useDismiss } from '../lib/useDismiss';
import { useTranslation } from 'react-i18next';
import type { OutgoingAttachment } from '../lib/agent';
import { ApiError, upload } from '../lib/api';
import { formatBytes, isVideoFile, prepareImage } from '../lib/image';
import {
  formatDuration,
  recorderSupport,
  startRecording,
  type VoiceRecorder,
} from '../lib/recorder';
import { setComposerFocus } from '../lib/viewport';
import { Camera, Close, FileIcon, Mic, Photo, Plus, Send, Spinner } from './ui/icons';

/** A file chosen or recorded, uploading or ready to send with the next message. */
interface Pending {
  key: string;
  file: File;
  kind: 'image' | 'document' | 'audio';
  previewUrl?: string | undefined;
  progress: number;
  attachment?: Attachment | undefined;
  error?: string | undefined;
}

const MAX_PENDING = 4;

/**
 * Message composer. Sits at the bottom edge of the visual viewport (the fixed root sizes
 * itself to the keyboard, see lib/viewport.ts). No autofocus on route load: the keyboard opens
 * only when the person taps the field. 16px text so iOS never zooms; enterkeyhint="send".
 *
 * M2: a "+" attach menu (photo library, camera, file) at the start of the bar and a quiet mic
 * button at the end when there is nothing to send. Voice notes: tap to start, tap to send,
 * slide or tap the cancel control to discard; the two-circle mark reacts to the input level.
 */
export function Composer({
  disabled,
  onSend,
}: {
  disabled: boolean;
  onSend: (text: string, attachments?: OutgoingAttachment[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState('');
  const [pending, setPending] = useState<Pending[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  // Outside pointer down, Escape, a scroll of the message list, all close it (M4.5).
  useDismiss(menuOpen, menuRef, closeMenu, { ignore: menuButtonRef });
  const [recorder, setRecorder] = useState<VoiceRecorder | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  /** Inline "Not yet." note when a video is picked, dropped or pasted. */
  const [notYet, setNotYet] = useState<string | null>(null);
  const [sendingVoice, setSendingVoice] = useState(false);
  const libraryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const support = recorderSupport();

  // Clear the focus marker if the composer unmounts while focused (route change).
  useEffect(() => () => setComposerFocus(false), []);
  useEffect(
    () => () => {
      recorder?.cancel();
    },
    [recorder],
  );

  const ready = pending.filter(
    (p): p is Pending & { attachment: Attachment } => p.attachment !== undefined,
  );
  const uploading = pending.some((p) => !p.attachment && !p.error);
  const canSend = !disabled && !uploading && (text.trim().length > 0 || ready.length > 0);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed && ready.length === 0) return;
    onSend(
      trimmed,
      ready.map((p) => ({ attachment: p.attachment, localUrl: p.previewUrl })),
    );
    setText('');
    setPending((prev) => prev.filter((p) => !p.attachment));
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSend) submit();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) submit();
    }
  };

  const addFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      const room = MAX_PENDING - pending.length;
      const list = [...files];
      // Video is announced, never silently ignored: the honest "Not yet." note.
      if (list.some((f) => isVideoFile(f))) setNotYet(t('two.attach.video'));
      for (const raw of list.filter((f) => !isVideoFile(f)).slice(0, Math.max(0, room))) {
        const isPdf = raw.type === 'application/pdf';
        const isImage = raw.type.startsWith('image/');
        if (!isPdf && !isImage) continue;
        const key = crypto.randomUUID();
        const file = isImage ? await prepareImage(raw) : raw;
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
        const entry: Pending = {
          key,
          file,
          kind: isPdf ? 'document' : 'image',
          previewUrl,
          progress: 0,
        };
        setPending((prev) => [...prev, entry]);
        upload(file, {
          filename: file.name,
          onProgress: (fraction) =>
            setPending((prev) =>
              prev.map((p) => (p.key === key ? { ...p, progress: fraction } : p)),
            ),
        })
          .then((attachment) =>
            setPending((prev) => prev.map((p) => (p.key === key ? { ...p, attachment } : p))),
          )
          .catch((err: unknown) => {
            const code = err instanceof ApiError ? err.code : 'network';
            setPending((prev) =>
              prev.map((p) =>
                p.key === key
                  ? { ...p, error: t(`two.upload.${code}`, { defaultValue: t('common.error') }) }
                  : p,
              ),
            );
          });
      }
    },
    [pending.length, t],
  );

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    setMenuOpen(false);
    void addFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: DragEvent<HTMLFormElement>) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    void addFiles(e.dataTransfer.files);
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      void addFiles(files);
    }
  };

  const remove = (key: string) =>
    setPending((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.key !== key);
    });

  // --- voice notes -----------------------------------------------------------

  const startVoice = async () => {
    setRecordError(null);
    if (support !== 'ok') {
      setRecordError(
        t(support === 'ios_too_old' ? 'two.voice.iosTooOld' : 'two.voice.unsupported'),
      );
      return;
    }
    try {
      setRecorder(await startRecording());
    } catch {
      setRecordError(t('two.voice.denied'));
    }
  };

  const cancelVoice = () => {
    recorder?.cancel();
    setRecorder(null);
  };

  const sendVoice = async () => {
    if (!recorder) return;
    setSendingVoice(true);
    try {
      const rec = await recorder.stop();
      setRecorder(null);
      const ext = rec.mime.includes('mp4') ? 'm4a' : rec.mime.includes('ogg') ? 'ogg' : 'webm';
      const file = new File([rec.blob], `voice-note.${ext}`, { type: rec.mime });
      const attachment = await upload(file, {
        duration_ms: rec.duration_ms,
        filename: file.name,
      });
      onSend('', [{ attachment, localUrl: URL.createObjectURL(rec.blob) }]);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'network';
      setRecordError(t(`two.upload.${code}`, { defaultValue: t('two.voice.failed') }));
      setRecorder(null);
    } finally {
      setSendingVoice(false);
    }
  };

  const showMic = text.trim().length === 0 && pending.length === 0 && !recorder;

  return (
    <form
      onSubmit={onSubmit}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="relative flex shrink-0 flex-col gap-2 border-t border-line bg-surface px-3 pb-safe pt-2 pb-2"
    >
      {pending.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label={t('two.attach.pending')}>
          {pending.map((p) => (
            <li
              key={p.key}
              className="relative flex items-center gap-2 rounded-md border border-line bg-canvas p-1.5 pe-9 text-xs text-ink"
            >
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="" className="size-10 rounded-sm object-cover" />
              ) : (
                <span className="flex size-10 items-center justify-center rounded-sm bg-surface text-ink-2">
                  <FileIcon size={18} />
                </span>
              )}
              <span className="flex max-w-36 flex-col">
                <span className="truncate" dir="auto">
                  {p.file.name}
                </span>
                <span className={p.error ? 'text-critical-text' : 'text-ink-3'}>
                  {p.error ??
                    (p.attachment
                      ? formatBytes(p.file.size, i18n.resolvedLanguage ?? 'en')
                      : `${Math.round(p.progress * 100)}%`)}
                </span>
              </span>
              {!p.attachment && !p.error ? <Spinner size={14} className="text-brand" /> : null}
              <button
                type="button"
                onClick={() => remove(p.key)}
                aria-label={t('two.attach.remove')}
                className="absolute end-0 top-0 flex size-9 items-center justify-center rounded-md text-ink-2 hover:text-ink"
              >
                <Close size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {recordError ? (
        <p role="alert" className="text-xs text-critical-text" dir="auto">
          {recordError}
        </p>
      ) : null}
      {notYet ? (
        <p role="status" className="flex items-start gap-2 text-sm text-ink-2" dir="auto">
          <span className="flex-1">{notYet}</span>
          <button
            type="button"
            onClick={() => setNotYet(null)}
            aria-label={t('common.close')}
            className="-my-1 flex size-8 shrink-0 items-center justify-center rounded-md text-ink-3 hover:text-ink"
          >
            <Close size={14} />
          </button>
        </p>
      ) : null}

      {recorder ? (
        <RecordingRow
          recorder={recorder}
          sending={sendingVoice}
          onCancel={cancelVoice}
          onSend={() => void sendVoice()}
        />
      ) : (
        <div className="flex items-end gap-2">
          <div className="relative">
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={t('two.attach.menu')}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-controls="attach-menu"
              disabled={disabled || pending.length >= MAX_PENDING}
              className="flex size-12 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-canvas hover:text-ink disabled:opacity-50"
            >
              <Plus size={22} />
            </button>
            {menuOpen ? (
              <div
                ref={menuRef}
                id="attach-menu"
                role="menu"
                className="absolute bottom-14 start-0 z-20 flex min-w-52 flex-col rounded-lg border border-line bg-surface p-1 shadow-lg dark:bg-elevated"
              >
                <MenuItem
                  icon={<Photo size={18} />}
                  label={t('two.attach.library')}
                  onClick={() => {
                    closeMenu();
                    libraryRef.current?.click();
                  }}
                />
                <MenuItem
                  icon={<Camera size={18} />}
                  label={t('two.attach.camera')}
                  onClick={() => {
                    closeMenu();
                    cameraRef.current?.click();
                  }}
                />
                <MenuItem
                  icon={<FileIcon size={18} />}
                  label={t('two.attach.file')}
                  onClick={() => {
                    closeMenu();
                    fileRef.current?.click();
                  }}
                />
              </div>
            ) : null}
          </div>
          <input ref={libraryRef} type="file" accept="image/*" multiple hidden onChange={onPick} />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={onPick}
          />
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            hidden
            onChange={onPick}
          />

          <textarea
            ref={textRef}
            dir="auto"
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => {
              setMenuOpen(false);
              setComposerFocus(true);
            }}
            onBlur={() => setComposerFocus(false)}
            enterKeyHint="send"
            autoCapitalize="sentences"
            placeholder={t('two.placeholder')}
            aria-label={t('two.placeholder')}
            className="control max-h-40 min-h-12 flex-1 resize-none rounded-md border border-line-strong bg-canvas px-3.5 py-3 text-base text-ink placeholder:text-ink-3"
          />

          {showMic ? (
            <button
              type="button"
              onClick={() => void startVoice()}
              disabled={disabled}
              aria-label={t('two.voice.start')}
              // Keep the keyboard where it is: the mic never steals focus.
              onPointerDown={(e) => e.preventDefault()}
              className="flex size-12 shrink-0 items-center justify-center rounded-md border border-line-strong bg-surface text-ink-2 transition-colors duration-[var(--pa-dur-fast)] hover:bg-canvas hover:text-ink disabled:opacity-50"
            >
              <Mic size={20} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              aria-label={t('two.send')}
              // Keep the textarea focused (and the keyboard open) when the send button is tapped.
              onPointerDown={(e) => e.preventDefault()}
              className="flex size-12 shrink-0 items-center justify-center rounded-md bg-brand text-on-brand shadow-sm transition-[background-color,transform] duration-[var(--pa-dur-fast)] hover:bg-brand-hover active:scale-[0.97] disabled:bg-line disabled:text-ink-3 disabled:shadow-none"
            >
              <Send size={20} />
            </button>
          )}
        </div>
      )}
    </form>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex min-h-11 items-center gap-3 rounded-md px-3 text-start text-sm text-ink hover:bg-canvas"
    >
      <span className="text-ink-2">{icon}</span>
      {label}
    </button>
  );
}

/**
 * Recording row: cancel (tap, or slide toward the start), the two-circle mark reacting to the
 * input level, a tabular timer, and send. Level and time are written through refs on each
 * animation frame so nothing re-renders sixty times a second.
 */
function RecordingRow({
  recorder,
  sending,
  onCancel,
  onSend,
}: {
  recorder: VoiceRecorder;
  sending: boolean;
  onCancel: () => void;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  const markRef = useRef<HTMLSpanElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const slideStart = useRef<number | null>(null);

  useEffect(() => {
    let frame = 0;
    const tick = () => {
      markRef.current?.style.setProperty('--level', String(recorder.level.toFixed(3)));
      if (timeRef.current)
        timeRef.current.textContent = formatDuration(Date.now() - recorder.startedAt);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [recorder]);

  const rtl = document.documentElement.dir === 'rtl';
  const onPointerDown = (e: React.PointerEvent) => {
    slideStart.current = e.clientX;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (slideStart.current === null) return;
    const dx = e.clientX - slideStart.current;
    // Sliding toward the start of the row (left in LTR, right in RTL) discards the note.
    if ((rtl ? dx : -dx) > 80) {
      slideStart.current = null;
      onCancel();
    }
  };
  const onPointerUp = () => {
    slideStart.current = null;
  };

  return (
    <div
      ref={rowRef}
      className="flex items-center gap-2"
      role="group"
      aria-label={t('two.voice.recording')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label={t('two.voice.cancel')}
        className="flex size-12 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-canvas hover:text-ink"
      >
        <Close size={20} />
      </button>
      <div className="flex min-h-12 flex-1 items-center gap-3 rounded-md border border-line-strong bg-canvas px-3.5">
        <span ref={markRef} className="record-mark" aria-hidden>
          <span className="record-mark-a" />
          <span className="record-mark-b" />
        </span>
        <span className="text-sm text-ink-2">{t('two.voice.hint')}</span>
        <span ref={timeRef} className="tabular ms-auto text-sm text-ink" dir="ltr">
          0:00
        </span>
      </div>
      <button
        type="button"
        onClick={onSend}
        disabled={sending}
        aria-label={t('two.voice.send')}
        className="flex size-12 shrink-0 items-center justify-center rounded-md bg-brand text-on-brand shadow-sm hover:bg-brand-hover active:scale-[0.97] disabled:bg-line disabled:text-ink-3"
      >
        {sending ? <Spinner size={20} /> : <Send size={20} />}
      </button>
    </div>
  );
}
